import { useRef, useState } from "react";
import { imageIdToUrl, InvalidImageError, uploadImage } from "../data";
import { Avatar, CircularProgress, Stack } from "@mui/material";
import AcceptOrCancel from "./base/AcceptOrCancel";
import ImageUploading, { ErrorsType, ImageListType, ImageType } from "react-images-uploading";

interface EditableAvatarProps {
    name: string;
    avatar_image_id: number | undefined;
    onCommit: (image_id: number) => Promise<void>;
}

export default function EditableAvatar({ name, avatar_image_id, onCommit }: EditableAvatarProps) {
    const request = useRef<number>(0);
    const [image, setImage] = useState<ImageType | undefined>(avatar_image_id ? { avatar_image_id } : undefined);
    const [isDisabled, setIsDisabled] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<unknown | undefined>(undefined);
    const hasPending = error || !!image?.["data_url"];

    const onChange = async (imageList: ImageListType, addUpdateIndex?: number[] | undefined) => {
        const requestId = ++request.current;
        setError(undefined);
        setLoading(false);

        // data for submit
        let image = imageList[0];
        setImage(image);

        if (image?.file) {
            try {
                setLoading(true);
                const imageId = await uploadImage(image.file);
                if (requestId !== request.current) return;
                setImage({ ...image, avatar_image_id: imageId });
            } catch (e) {
                if (requestId !== request.current) return;
                setError(e);
            } finally {
                if (requestId !== request.current) return;
                setLoading(false);
            }
        }
    };

    const onError = (errors: ErrorsType) => {
        if (!errors) {
            setError(undefined);
            return;
        }

        setError(new InvalidImageError(JSON.stringify(errors)));
    };

    const onSave = async () => {
        setIsDisabled(true);
        try {
            const image_id: number = image!.avatar_image_id;
            await onCommit(image_id);
            setImage(avatar_image_id ? { avatar_image_id: image_id } : undefined);
        } finally {
            setIsDisabled(false);
        }
    };

    const onCancel = () => {
        setImage(avatar_image_id ? { avatar_image_id } : undefined);
        setError(undefined);
    };

    return (
        <Stack alignItems="end" width="fit-content">
            <ImageUploading
                value={image ? [image] : []}
                onChange={onChange}
                onError={onError}
                dataURLKey="data_url"
                maxFileSize={2048 * 1024}
            >
                {({
                    imageList,
                    onImageUpload,
                    onImageRemoveAll,
                    onImageUpdate,
                    onImageRemove,
                    isDragging,
                    dragProps,
                }) => {
                    let classNames = ["upload_image_button-wrapper"];
                    if (isDragging) {
                        classNames.push("dragging");
                    }

                    const image = imageList[0];
                    const imageUrl = image?.["data_url"] || imageIdToUrl("large", image?.["avatar_image_id"]);
                    // write your building UI
                    return (
                        <Avatar
                            alt={name}
                            src={loading || error ? undefined : imageUrl}
                            onClick={isDisabled ? undefined : onImageUpload}
                            sx={{ width: 200, height: 200, cursor: "pointer" }}
                        >
                            {error ? (
                                <div className="error">
                                    {"" + (error instanceof InvalidImageError ? "Filen stöds ej" : error)}
                                </div>
                            ) : (
                                <></>
                            )}
                            {loading ? <CircularProgress /> : <></>}
                        </Avatar>
                    );
                }}
            </ImageUploading>
            {hasPending ? (
                <AcceptOrCancel
                    onAccept={error || loading ? undefined : onSave}
                    onCancel={onCancel}
                    isDisabled={isDisabled}
                ></AcceptOrCancel>
            ) : (
                <></>
            )}
        </Stack>
    );
}
